import { getSmartCollection } from 'api/product/getSmartCollection';
import { SmartCollectionResponse } from 'api/product/getSmartCollection/types';
import { resolveLocalizedCanonicalUrl } from 'components/seo/canonical/resolve-localized-canonical-url';
import {
  EntryWithHandle,
  fetchContentfulByHandle,
  fetchLocalizedHandles,
} from 'connectors/contentful';
import { IProductPagesFields } from 'global';
import CollectionHead from 'modules/collection/collection-head';
import { ContentfulProvider } from 'modules/content-cms/components/context';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { axios } from 'utils/axios';
import { getCollectionAnchor } from 'utils/collection/get-collection-anchor';
import { configureAxiosInstance } from 'utils/configure-axios';
import { getLocaleFromNextLocale, getRegionFromRouteParam } from 'utils/market';
import { hasH1TitleInCdp } from 'utils/contentful';
import CollectionWrapper from '../../../modules/collection';

type SegmentationId = string;
type AlterativeRibbonText = string;
export type AbTestRibbonTextObj = {
  [key in SegmentationId]?: AlterativeRibbonText;
};
interface PageProps {
  addedProductsData?: Record<string, number>;
  contentfulData: EntryWithHandle<IProductPagesFields>;
  canonicalUrl: string;
  handle: string;
  collection: SmartCollectionResponse;
  /** true if page entry has h1 title  */
  hasH1title: boolean;
  tags: string[];
}

const CollectionPage: NextPage<PageProps> = ({
  collection,
  contentfulData,
  canonicalUrl,
  handle,
  hasH1title,
  tags,
}) => {
  const router = useRouter();
  const { sortBy } = router.query;
  return (
    <ContentfulProvider fallbackData={contentfulData} handle={`collections/${handle}`}>
      <CollectionHead canonicalUrl={canonicalUrl} collection={collection} />
      <CollectionWrapper
        handle={handle}
        collection={collection}
        tags={tags}
        sortBy={sortBy as string}
        hasH1title={hasH1title}
      />
    </ContentfulProvider>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params, locale: nextLocale }) => {
  const region = getRegionFromRouteParam(params?.region);
  const locale = getLocaleFromNextLocale(params?.region, nextLocale);
  const cleanup = configureAxiosInstance(axios, region, locale);

  const all = params?.all || [];
  const [handle, encodedTags] = Array.isArray(all) ? all : [all, undefined];
  const tags = encodedTags ? decodeURI(encodedTags).split('+') : [];

  try {
    const contentfulData = await fetchContentfulByHandle({
      handle: `collections/${handle}`,
      locale,
      region,
    });

    if (contentfulData === null) {
      return { notFound: true, revalidate: true };
    }

    const anchorHandle = getCollectionAnchor({
      handle: contentfulData.handle,
      fallback: handle,
    });

    const [collection, localizedHandles] = await Promise.all([
      getSmartCollection({ slug: anchorHandle }),
      fetchLocalizedHandles(contentfulData.sys.id).catch((error) => {
        console.error('[Collection Page] Failed to fetch localized handles:', error);
        return {};
      }),
    ]);

    if (collection === null || collection.title === '') {
      return { notFound: true, revalidate: true };
    }

    const hasH1title = hasH1TitleInCdp({ contentful: contentfulData });
    const canonicalUrl = resolveLocalizedCanonicalUrl({
      region,
      locale,
      fallbackPath: contentfulData.deHandle || contentfulData.handle,
      localizedPaths: localizedHandles,
      canonical: contentfulData.fields.canonical,
    });

    return {
      props: {
        collection,
        contentfulData,
        canonicalUrl,
        handle,
        hasH1title,
        tags,
      },
      revalidate: 300, // 5 minutes
    };
  } catch (error) {
    console.log(error);
    console.error(`Error while prefetching data for collection with handle: ${handle}`);
    return { notFound: true, revalidate: true };
  } finally {
    cleanup();
  }
};
export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export default CollectionPage;
